/**
 * What "What we noticed" shows for one organisation: the closer read's
 * consolidated points first, then every check none of them absorbed (a
 * check that arrived after the read, or one the read left as it is).
 * Mirrors `org_findings.apply_closer_read` on the backend.
 */
export function applyCloser(checks, closer) {
  const points = closer?.findings || [];
  const absorbed = new Set([...(closer?.absorbed || []), ...points.flatMap((p) => p.merges || [])]);
  const seen = new Set();
  return [...points, ...(checks || []).filter((f) => !absorbed.has(f.id))].filter((f) => {
    if (seen.has(f.id)) return false;
    seen.add(f.id);
    return true;
  });
}

// A redaction where a name should be (mirrors record_findings._REDACTED).
const REDACTED = /\[[^\]]{0,30}redact[^\]]{0,30}\]|\[name[^\]]{0,20}\]|█|■|\bX{3,}\b/i;

/**
 * Whether a flagged concern or framing passage is about someone other than
 * the person (mirrors record_findings.about_someone_else): the extraction
 * says so, or — for framing, whatever it said — the quote's subject is
 * redacted, which their own name never is in their own records.
 */
export function aboutSomeoneElse(item, key) {
  if (key === 'framing' && REDACTED.test(item?.quote || '')) return true;
  return item?.about === 'someone_else';
}

/** A record's detection with only what's about the person themselves. */
export function aboutThem(detection) {
  return {
    framing: (detection?.framing || []).filter((f) => !aboutSomeoneElse(f, 'framing')),
    concerns: (detection?.concerns || []).filter((c) => !aboutSomeoneElse(c, 'concerns')),
  };
}

/**
 * The drawers of the "What we noticed" tab, most pressing first (the same
 * order as the closer read's `_CATEGORY_ORDER` in services/file_review.py).
 * Worded as what the person is looking at, never as a verdict.
 */
export const FINDING_CATEGORIES = [
  { id: 'concern', label: 'Worries raised', hint: 'Things you or others were worried about, and what the records show happened next.' },
  { id: 'framing', label: 'How you were written about', hint: 'Words that may have questioned you or played things down.' },
  { id: 'process', label: 'What should have happened', hint: 'Reviews, visits and plans, checked against the rules at the time.' },
  { id: 'pattern', label: 'Patterns', hint: 'Things that only show when you look across years.' },
  { id: 'completeness', label: 'What might be missing', hint: 'Records the rules say should exist but aren’t in what you hold.' },
];
const CATEGORY_RANK = Object.fromEntries(FINDING_CATEGORIES.map((c, i) => [c.id, i]));
export const categoryOf = (f) => (CATEGORY_RANK[f?.category] !== undefined ? f.category : 'completeness');
export const categoryLabel = (id) => FINDING_CATEGORIES.find((c) => c.id === id)?.label || 'Something to check';

/** How sure a finding is (its `strength`), strongest first. */
export const FINDING_STRENGTHS = [
  { id: 'statutory', label: 'Required by law' },
  { id: 'expected', label: 'Good practice' },
  { id: 'pattern', label: 'Patterns' },
  { id: 'check', label: 'To check' },
];
const STRENGTH_RANK = Object.fromEntries(FINDING_STRENGTHS.map((s, i) => [s.id, i]));
export const strengthOf = (f) => (STRENGTH_RANK[f?.strength] !== undefined ? f.strength : 'check');

/** Most pressing first: by drawer, then how sure, keeping the listed order within that. */
export const byImportance = (a, b) => (CATEGORY_RANK[categoryOf(a)] - CATEGORY_RANK[categoryOf(b)])
  || (STRENGTH_RANK[strengthOf(a)] - STRENGTH_RANK[strengthOf(b)]);
export const byCertainty = (a, b) => (STRENGTH_RANK[strengthOf(a)] - STRENGTH_RANK[strengthOf(b)])
  || (CATEGORY_RANK[categoryOf(a)] - CATEGORY_RANK[categoryOf(b)]);

/** The earliest date a finding is about, as a sortable string ('' if none). */
export const earliestDate = (f) => {
  const dates = (f?.evidence || []).map((e) => e.date).filter(Boolean).sort();
  if (dates.length) return String(dates[0]);
  return f?.years?.length ? String(Math.min(...f.years)) : '';
};

/** "2014" or "2011–2014" from a finding's years, or null. */
export const yearSpan = (f) => {
  const years = [...new Set((f?.years || []).filter(Boolean))].sort();
  if (!years.length) return null;
  return years.length === 1 ? String(years[0]) : `${years[0]}–${years[years.length - 1]}`;
};

/**
 * What the "Dismissed" drawer lists: everything put aside, less the checks
 * a closer-read point absorbed (they're shown, and put back, through it) —
 * the same rule as applyCloser, for the dismissed side.
 */
export function visibleDismissed(dismissed, closer) {
  const points = [...(closer?.findings || []), ...(dismissed || []).filter((d) => d.merges?.length)];
  const absorbed = new Set([...(closer?.absorbed || []), ...points.flatMap((p) => p.merges || [])]);
  const seen = new Set();
  return (dismissed || []).filter((d) => {
    if (absorbed.has(d.id) || seen.has(d.id)) return false;
    seen.add(d.id);
    return true;
  });
}
